import React, { Suspense, useEffect } from "react";
import { HashRouter, Route, Switch } from "react-router-dom";
import ErrorBoundary from "./ErrorBoundary";
import { useDispatch, useSelector } from "react-redux";
import { selectAuthToken } from "../reducers";
import { login, selectSecurityStatus } from "../reducers/securitySlice";
import GenericUnauthenticated from "./Unauthenticated";
import IdleTimer from "../components/IdleTimer";
import GlobalLoading from "./GlobalLoading";
import LeaveClaim from "../features/cme/admin/LeaveClaim";
import CapexQna from "../features/capex/CapexQna";

const CmeWrapper = React.lazy(() =>
	import("../features/cme/Components/CmeWrapper")
);
const FormApproval = React.lazy(() => import("../features/cme/formApproval"));
const FormRedirector = React.lazy(() =>
	import("features/cme/formJumper/FormRedirector")
);
const CapexDashboard = React.lazy(() =>
	import("../features/capex/CapexDashboard")
);
const Dashboard = React.lazy(() => import("../features/cme/dashboard"));
const MyHistory = React.lazy(() => import("../features/cme/myHistory"));
const ManageForms = React.lazy(() =>
	import("../features/cme/admin/ManageForms")
);
const Cme = React.lazy(() => import("../features/cme"));
const Balance = React.lazy(() => import("../features/cme/balance"));
const Unauthenticated = React.lazy(() =>
	import("../features/cme/unauthenticated")
);
const UnauthenticatedCapex = React.lazy(() =>
	import("../features/capex/unauthenticated")
);
const Admin = React.lazy(() => import("../features/cme/admin"));
const CapexAdmin = React.lazy(() => import("../features/capex/admin"));
const Payroll = React.lazy(() => import("../features/cme/payroll/Payroll"));
const PayrollDashboard = React.lazy(() =>
	import("../features/cme/payroll/Dashboard")
);
const Coordinator = React.lazy(() =>
	import("../features/cme/admin/Coordinator")
);

const CapexWrapper = React.lazy(() => import("../features/capex/CapexWrapper"));
const PayrollFormJumper = React.lazy(() =>
	import("../features/cme/payroll/PayrollFormJumper")
);

const RocWrapper = React.lazy(() => import("features/roc/RocWrapper"));
const RoC = React.lazy(() => import("features/roc/Roc"));

const Router = () => {
	//establish user and token in state.security reducer
	const hasToken = useSelector(selectAuthToken);
	const securityStatus = useSelector(selectSecurityStatus);
	const dispatch = useDispatch();
	useEffect(() => {
		if (!hasToken) dispatch(login());
	}, [dispatch, hasToken]);
	return (
		<HashRouter>
			<ErrorBoundary>
				{process.env.REACT_APP_ENVIRONMENT === "AZURE" ? (
					<IdleTimer />
				) : null}
				<Suspense fallback={<GlobalLoading />}>
					{hasToken ? (
						<Switch>
							<Route path="/form/:formDefinitionId?/:formId?">
								<CapexWrapper>
									<CapexQna />
								</CapexWrapper>
							</Route>
							<Route path="/RoC">
								<RocWrapper>
									<RoC />
								</RocWrapper>
							</Route>
							<Route path="/Capex/Dashboard">
								<CapexWrapper>
									<CapexDashboard />
								</CapexWrapper>
							</Route>
							<Route path="/Capex/Admin/:screen?/:id?">
								<CapexWrapper>
									<CapexAdmin />
								</CapexWrapper>
							</Route>
							<Route path="/CME/Dashboard">
								<CmeWrapper>
									<Dashboard />
								</CmeWrapper>
							</Route>
							<Route path="/CME/Balance/:mode/:personId?">
								<CmeWrapper>
									<Balance />
								</CmeWrapper>
							</Route>
							<Route path="/CME/MyHistory/:mode?">
								<CmeWrapper>
									<MyHistory />
								</CmeWrapper>
							</Route>
							<Route path="/CME/Form/:formDefinitionId?/:formId?">
								<CmeWrapper>
									<Cme />
								</CmeWrapper>
							</Route>
							<Route path="/CME/Payroll/Find">
								<CmeWrapper>
									<PayrollFormJumper />
								</CmeWrapper>
							</Route>
							<Route path="/CME/Payroll/Dashboard">
								<CmeWrapper>
									<PayrollDashboard />
								</CmeWrapper>
							</Route>
							<Route path="/CME/Payroll/:formDefinitionId?/:formId?">
								<CmeWrapper>
									<Payroll />
								</CmeWrapper>
							</Route>
							<Route path="/CME/New/:formDefinitionId?/:formTypeId?/:parentFormId?">
								<CmeWrapper>
									<Cme />
								</CmeWrapper>
							</Route>
							<Route path="/CME/Admin/ManageForms/:personId">
								<CmeWrapper>
									<ManageForms />
								</CmeWrapper>
							</Route>
							<Route path="/CME/Admin/:screen?/:id?">
								<CmeWrapper>
									<Admin />
								</CmeWrapper>
							</Route>
							<Route path="/CME/Coordinator/Leave/:formDefinitionId?/:formId?">
								<CmeWrapper>
									<LeaveClaim />
								</CmeWrapper>
							</Route>
							<Route path="/CME/Coordinator/:screen?/:id?">
								<CmeWrapper>
									<Coordinator />
								</CmeWrapper>
							</Route>
							<Route path="/CME/ApproverDashboard/:personId?">
								<CmeWrapper>
									<FormApproval />
								</CmeWrapper>
							</Route>
							<Route path="/CME/FormRedirector/:formId?">
								<CmeWrapper>
									<FormRedirector />
								</CmeWrapper>
							</Route>
							<Route path="/CME/Unauthenticated/:error?">
								<Unauthenticated />
							</Route>
							<Route path="/CAPEX/Unauthenticated/:error?">
								<UnauthenticatedCapex />
							</Route>
						</Switch>
					) : securityStatus === "failed" ? (
						<GenericUnauthenticated />
					) : (
						<GlobalLoading />
					)}
				</Suspense>
			</ErrorBoundary>
		</HashRouter>
	);
};

export default Router;
